import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getBrowserSupabaseClient } from "@west-tennessee-consulting/supabase";
import { insertWithUserId } from "@west-tennessee-consulting/supabase";
import { useUniversalAuth } from "@west-tennessee-consulting/ui";
import type { ContractGenerationData, ContractGenerationResult } from "@/lib/types/contracts";

export function useContractGeneration() {
  const queryClient = useQueryClient();
  const { session } = useUniversalAuth();

  return useMutation<ContractGenerationResult, Error, ContractGenerationData>({
    mutationFn: async (data: ContractGenerationData) => {
      const token = session?.access_token;
      if (!token) {
        throw new Error("Authentication required");
      }

      // Step 1: Generate contract via eSignature API
      const response = await fetch("/api/contracts/generate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Contract generation failed");
      }

      // Step 2: Save contract proposal to database
      if (data.clientId && !data.previewSelf) {
        const supabase = getBrowserSupabaseClient();

        const proposalData = {
          contact_id: data.clientId,
          title: result.data.documentName,
          description: `Contract generated from ${data.schemaId} template`,
          amount: data.clientData?.hourly_rate ?? data.clientData?.monthly_rate ?? 0,
          currency: "USD",
          status: "sent" as const,
          sent_at: new Date().toISOString(),
          file_url: result.data.signingUrl || "",
          file_name: `${result.data.documentName}.pdf`,
          terms_and_conditions: JSON.stringify(data.formData),
          notes: `eSignature Envelope ID: ${result.data.envelopeId}`,
        };

        // Use the utility that automatically adds user_id
        const { error: proposalError } = await insertWithUserId(
          supabase,
          "contact_proposals",
          proposalData
        );

        if (proposalError) {
          // The contract was generated/sent successfully, but the local
          // proposal record failed to save. Don't fail the whole request —
          // but surface it so the user knows the record may be missing.
          toast.warning(
            "Contract was sent, but saving the proposal record failed. It may not appear in the contracts list."
          );
        }
      }

      return result;
    },
    onSuccess: (data, variables) => {
      // Invalidate related queries
      void queryClient.invalidateQueries({ queryKey: ["contact-proposals"] });
      void queryClient.invalidateQueries({
        queryKey: ["contacts", variables.clientId],
      });
    },
    onError: (error) => {
      toast.error(`Contract generation failed: ${error.message}`);
    },
  });
}
